import { ReactNode, useCallback, useMemo } from 'react';

import { rem, Box, Flex, Paragraph, useColorMode, Text } from '@vimeo/bokeh';
import { PinOnFilled } from '@vimeo/bokeh-icons';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';

import { default as Avatar } from '../../../../components/illustrations/Avatar.svg';
import { T_ANONYMOUS } from '../../../../translations';
import {
  EQuestionState,
  IQnAQuestion,
  IQnAQuestionReply,
  TQnAQuestionReplies,
} from '../../../../types/interaction_tools';
import { createDomName } from '../../../../utils/dom/identifying';
import { CdnAvatar } from '../../../avatar';
import { NewQuestionItemReply } from './NewQuestionItemReply';
import {
  createQuestionInfoStyle,
  TIME_ASKED_STYLE,
  createAvatarWrapperStyle,
  createQuestionItemWrapperStyle,
} from './QuestionItem.styles';
import { QuestionItemAuthor } from './QuestionItemAuthor';
import { QuestionItemVoteButton } from './QuestionItemVoteButton';
import { useQuestionTimeAsked } from './useQuestionTimeAsked';

interface IQuestionItemPreviewProps extends IBaseDomElementProps {
  question: IQnAQuestion;
  isPinned?: boolean;
  hasVoteButton?: boolean;
  questionReplies?: TQnAQuestionReplies;
  onQuestionVoted?: (questionId: string) => Promise<void>;
}

export function NewQuestionViewItem({
  className = createDomName('qna-item-preview'),
  question,
  isPinned = false,
  hasVoteButton = false,
  questionReplies = {},
  onQuestionVoted,
}: IQuestionItemPreviewProps) {
  const { id, text, createdAt, anonymous, votesCount, state, user } = question;

  const { colorMode: themeName } = useColorMode();

  const timeAsked: string = useQuestionTimeAsked(createdAt);

  const name: string = anonymous || !user || !user.displayName ? T_ANONYMOUS : user.displayName;
  const avatar: ReactNode =
    anonymous || !user?.avatarUrl ? (
      <Avatar height={'sm'} width={'sm'} />
    ) : (
      <CdnAvatar src={user.avatarUrl} size={'sm'} name={name} />
    );

  const canVote: boolean =
    hasVoteButton && (state === EQuestionState.ASKED || state === EQuestionState.ANSWERING);
  const canSeeVotes: boolean = state !== EQuestionState.PENDING && (canVote || votesCount > 0);
  const questionRepliesList: Array<IQnAQuestionReply> = Object.values(questionReplies);
  const canBePinned: boolean =
    state !== EQuestionState.ARCHIVED && state !== EQuestionState.PENDING;

  const itemClassName: string = useMemo(
    () => `${className} ${canBePinned && isPinned ? 'pinned-item' : ''}`,
    [className, canBePinned, isPinned],
  );

  const onVoteQuestion = useCallback(async () => {
    if (onQuestionVoted) {
      await onQuestionVoted(id);
    }
  }, [id, onQuestionVoted]);

  return (
    <Flex
      className={itemClassName}
      sx={createQuestionItemWrapperStyle({
        themeName,
        isPinned: canBePinned && isPinned,
      })}
    >
      {canBePinned && isPinned ? (
        <Flex
          justifyContent={'center'}
          alignItems={'center'}
          height={rem(24)}
          width={rem(24)}
          padding={`0 ${rem(4)}`}
          position={'absolute'}
          top={rem(8)}
          right={rem(8)}
        >
          <PinOnFilled boxSize={rem(16)} />
        </Flex>
      ) : null}

      <Box className={createDomName(className, 'author-avatar')} sx={createAvatarWrapperStyle()}>
        {avatar}
      </Box>

      <Flex
        flexDirection={'column'}
        alignItems={'flex-start'}
        width={'100%'}
        gap={rem(8)}
        overflow={'hidden'}
      >
        <Flex
          className={createDomName(className, 'author-info')}
          sx={{ ...createQuestionInfoStyle(), paddingRight: rem(isPinned ? 24 : 0) }}
        >
          <QuestionItemAuthor className={createDomName(className, 'author-name')} name={name} />

          <Text
            className={createDomName(className, 'time')}
            variant={'body-sm'}
            sx={TIME_ASKED_STYLE}
          >
            {timeAsked}
          </Text>
        </Flex>

        <Paragraph
          className={createDomName(className, 'text')}
          variant={'body-md'}
          maxWidth={'100%'}
        >
          {text}
        </Paragraph>

        {canSeeVotes ? (
          <QuestionItemVoteButton
            className={createDomName(className, 'votes')}
            isDisabled={!canVote}
            votesCount={votesCount}
            onVoteQuestion={onVoteQuestion}
          />
        ) : null}

        {questionRepliesList.map((it, index) => (
          <NewQuestionItemReply key={index} reply={it} />
        ))}
      </Flex>
    </Flex>
  );
}
